// /app/frontend/src/pages/compliance/maturity/MaturityDashboardPage.jsx
/**
 * TR2UST v2.0 - Maturity Assessment Dashboard
 * Swiss & High-Contrast Design System with Dark Mode Support
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useGlobalContext } from '../../../contexts/GlobalContext';
import { maturityAPI, tasksAPI } from '../../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { AutoTaskPromptModal } from '../../../components/shared/AutoTaskPromptModal';
import {
  Shield, Target, FileText, Download, ArrowRight, Building2, ChevronRight,
  Home, Database, Lock, Users, Briefcase, Bell, Scale, TrendingUp,
  CheckCircle, AlertCircle, Clock, ListChecks, Plus
} from 'lucide-react';
import { format } from 'date-fns';
import { SCORE_RELIABILITY_THRESHOLD, NA_ADVISORY_THRESHOLD, TIER_BANNER } from './maturityConfig';

// Domain icons and colors
const DOMAIN_CONFIG = {
  GOV: { icon: Scale, color: '#3B82F6', name: 'Governance' },
  RCT: { icon: Users, color: '#8B5CF6', name: 'Rights & Consent' },
  DLM: { icon: Database, color: '#06B6D4', name: 'Data Lifecycle' },
  SEC: { icon: Lock, color: '#10B981', name: 'Security' },
  TPM: { icon: Briefcase, color: '#F59E0B', name: 'Third Party' },
  INC: { icon: Bell, color: '#EF4444', name: 'Incident Mgmt' }
};

// Maturity level configuration
const MATURITY_LEVELS = {
  1: { name: 'Reactive', color: '#EF4444', bg: 'bg-red-500/10 dark:bg-red-500/20', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-500/30' },
  2: { name: 'Provisional', color: '#F59E0B', bg: 'bg-amber-500/10 dark:bg-amber-500/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-500/30' },
  3: { name: 'Formalized', color: '#10B981', bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-500/30' },
  4: { name: 'Monitored', color: '#3B82F6', bg: 'bg-blue-500/10 dark:bg-blue-500/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-500/30' },
  5: { name: 'Proactive', color: '#8B5CF6', bg: 'bg-violet-500/10 dark:bg-violet-500/20', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-500/30' }
};

// Clean Arc Gauge Component
function ArcGauge({ score = 0, size = 140, domainColor = '#3B82F6' }) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size / 2 + 20 }}>
      <svg width={size} height={size / 2 + 10} className="transform -rotate-0">
        <path
          d={`M ${strokeWidth / 2} ${center} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${center}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="text-slate-200 dark:text-slate-700"
        />
        <path
          d={`M ${strokeWidth / 2} ${center} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${center}`}
          fill="none"
          stroke={domainColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-end justify-center pb-2">
        <span className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {score}%
        </span>
      </div>
    </div>
  );
}

// Domain Card Component
function DomainCard({ domain, domainScore, onClick }) {
  const config = DOMAIN_CONFIG[domain.code] || { icon: Shield, color: '#64748B', name: domain.name };
  const Icon = config.icon;
  const score = domainScore?.score || 0;
  const level = domainScore?.maturity_level || 1;
  const levelConfig = MATURITY_LEVELS[level];
  const assessed = domainScore?.applicable_controls || 0;
  const total = domainScore?.total_controls || domain.control_count;  // Use tier-filtered count from API
  const remaining = total - assessed - (domainScore?.na_controls || 0);

  // Score Integrity Signals (Change 3)
  const touchedControls = (domainScore?.applicable_controls || 0) + (domainScore?.na_controls || 0);
  const totalControls = domainScore?.total_controls || domain.control_count || 0;
  const completionRatio = totalControls > 0 ? touchedControls / totalControls : 0;
  const isIncomplete = completionRatio < SCORE_RELIABILITY_THRESHOLD;
  
  return (
    <Card 
      onClick={onClick}
      className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-blue-300/50 dark:hover:border-blue-500/50 transition-all duration-200 cursor-pointer overflow-hidden"
      data-testid={`domain-card-${domain.code}`}
    >
      <CardContent className="p-0">
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${config.color}20` }}
              >
                <Icon className="h-5 w-5" style={{ color: config.color }} strokeWidth={1.5} />
              </div>
              <div>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {domain.code} • {Math.round(domain.weight * 100)}%
                </span>
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {domain.name}
                </h3>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center px-5">
          <div className={isIncomplete ? 'opacity-50' : ''}>
            <ArcGauge score={score} size={140} domainColor={config.color} />
          </div>
        </div>

        <div className="flex justify-center -mt-1 mb-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${levelConfig.bg} ${levelConfig.text} ${levelConfig.border} border`}>
            {levelConfig.name}
          </span>
        </div>

        {/* Score Integrity Signals - Completion Ratio */}
        <div className="text-center px-5">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {touchedControls} of {totalControls} controls assessed
          </p>
          {isIncomplete && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Score may not be representative — assessment incomplete.
            </p>
          )}
        </div>
        
        <div className="px-5 pb-4">
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">{assessed}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Assessed</div>
              </div>
              <div>
                <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{remaining}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Pending</div>
              </div>
              <div>
                <div className="text-lg font-bold text-slate-400">{domainScore?.na_controls || 0}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">N/A</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-5 py-3 bg-slate-50/50 dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-700 group-hover:bg-blue-50/50 dark:group-hover:bg-blue-900/20 transition-colors">
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            <span>Assess Controls</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Overall Score Card
function OverallScoreCard({ score, level, status }) {
  const levelConfig = MATURITY_LEVELS[level] || MATURITY_LEVELS[1];
  
  return (
    <Card className="bg-slate-900 dark:bg-slate-950 text-white rounded-xl border-0 shadow-xl overflow-hidden" data-testid="overall-score-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
              Overall Maturity
            </div>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-5xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {score || 0}%
              </span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-semibold"
                style={{ backgroundColor: levelConfig.color, color: 'white' }}
              >
                Level {level}: {levelConfig.name}
              </span>
            </div>
            <p className="text-slate-400 text-sm max-w-md">
              {status || 'Complete your assessment to get compliance recommendations'}
            </p>
          </div>
          <div className="hidden md:flex items-center justify-center w-20 h-20 rounded-2xl bg-white/10">
            <Target className="h-10 w-10 text-white/80" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Progress Card
function ProgressCard({ total, assessed, remaining, na }) {
  const progressPercent = total > 0 ? Math.round((assessed / total) * 100) : 0;
  
  return (
    <Card className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="progress-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wider mb-1">
              Assessment Progress
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {progressPercent}% Complete
            </div>
          </div>
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
            <Clock className="h-4 w-4" strokeWidth={1.5} />
            <span className="text-sm font-medium">{remaining} remaining</span>
          </div>
        </div>
        
        <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <div className="text-xl font-bold text-slate-900 dark:text-white">{total}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Total</div>
          </div>
          <div className="text-center p-3 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-lg">
            <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{assessed}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Assessed</div>
          </div>
          <div className="text-center p-3 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg">
            <div className="text-xl font-bold text-amber-600 dark:text-amber-400">{remaining}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Pending</div>
          </div>
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <div className="text-xl font-bold text-slate-400">{na}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">N/A</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Spec Section 6 — Assessments tasks section.
 * Shows remediation tasks aggregated across ALL snapshots for the entity.
 * Client chose Option B: tasks link to snapshot_id, dashboard queries across all of them.
 */
function AssessmentTasksSection({ tasks, loading, latestSnapshotId, clientId, isDpoRole, onNavigate }) {
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const percent = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const handleCreate = () => {
    const params = new URLSearchParams({ source_module: 'assessment', source_id: latestSnapshotId });
    if (clientId) params.set('client_id', clientId);
    onNavigate(`/operations/tasks?${params.toString()}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="h-4 w-4" />
          Remediation Tasks
          {tasks.length > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
              {tasks.length}
            </span>
          )}
        </CardTitle>
        {isDpoRole && latestSnapshotId && (
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Create task
          </button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">Loading tasks…</p>
        ) : tasks.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">
            No remediation tasks yet. Save a snapshot to generate task suggestions.
          </p>
        ) : (
          <>
            {/* Progress bar — Spec Section 6: "findings vs completed" */}
            <div className="mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {completedCount} of {tasks.length} complete
                </p>
                <span className="text-sm text-slate-500 dark:text-slate-400">{percent}%</span>
              </div>
              <Progress value={percent} className="h-2" />
            </div>

            {/* Task rows */}
            <div className="space-y-2">
              {tasks.map(t => {
                const isOverdue = t.due_date && t.status !== 'completed' && new Date(t.due_date) < new Date();
                return (
                  <div
                    key={t.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                    onClick={() => onNavigate(`/operations/tasks?task=${t.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{t.title}</p>
                      {t.due_date && (
                        <p className={`text-xs mt-0.5 ${isOverdue ? 'text-red-500' : 'text-slate-500 dark:text-slate-400'}`}>
                          Due {format(new Date(t.due_date), 'MMM d, yyyy')}
                          {isOverdue && ' · Overdue'}
                        </p>
                      )}
                    </div>
                    <span className={`ml-3 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize shrink-0 ${
                      t.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                      t.status === 'in_progress' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                      t.status === 'blocked' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                      'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                    }`}>
                      {t.status?.replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function MaturityDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedClientId, selectedEntityId } = useGlobalContext();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [domains, setDomains] = useState([]);
  const [score, setScore] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [taskPromptOpen, setTaskPromptOpen] = useState(false);
  const [taskPromptSnapshotId, setTaskPromptSnapshotId] = useState(null);
  const [assessmentTasks, setAssessmentTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [latestSnapshotId, setLatestSnapshotId] = useState(null);
  const isDpoRole = ['dpo', 'super_admin'].includes(user?.role);

  useEffect(() => {
    const handleRouteState = () => {
      // If route state has entity_id from Readiness Assessment, it will override context selection
      const { entity_id } = location.state || {};
      if (entity_id && selectedEntityId !== entity_id) {
        // Note: Route state override would require additional state management
        // For now, context selectedEntityId takes precedence
      }
    };
    handleRouteState();
  }, [location.state, selectedEntityId]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const [configRes, domainsRes] = await Promise.all([
          maturityAPI.getConfig(),
          maturityAPI.getDomains()
        ]);
        setConfig(configRes.data);
        setDomains(domainsRes.data);
      } catch (error) {
        toast.error('Failed to load configuration');
      }
    };
    loadConfig();
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedEntityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [scoreRes, tierRes] = await Promise.all([
        maturityAPI.getScore(selectedEntityId, selectedClientId || undefined).catch(() => ({ data: null })),
        maturityAPI.getTier(selectedEntityId, selectedClientId || undefined).catch(() => ({ data: null }))
      ]);
      setScore(scoreRes.data);
      setTierInfo(tierRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedClientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadAssessmentTasks = useCallback(async () => {
    if (!selectedEntityId) return;
    setTasksLoading(true);
    try {
      const res = await tasksAPI.getByAssessmentEntity(selectedEntityId);
      setAssessmentTasks(res.data.tasks || []);
      setLatestSnapshotId(res.data.latest_snapshot_id || null);
    } catch (err) {
      console.error('Failed to load assessment tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  }, [selectedEntityId]);

  useEffect(() => {
    loadAssessmentTasks();
  }, [loadAssessmentTasks]);

  // Check for snapshot prompt on mount / when returning to page (Change 4).
  // Fire inline — the previous setTimeout-based approach was fragile under
  // React StrictMode's double-invocation (the first setup set the flag, the
  // re-run saw it as already set and the scheduled timer could be cancelled
  // by cleanup). Toaster is globally mounted so synchronous firing is fine.
  useEffect(() => {
    const checkSnapshotPrompt = () => {
      const hasAssessed = sessionStorage.getItem('maturityAssessedInSession') === 'true';
      const alreadyPrompted = sessionStorage.getItem('snapshotPromptShown') === 'true';
      if (hasAssessed && !alreadyPrompted && selectedEntityId) {
        sessionStorage.setItem('snapshotPromptShown', 'true');
        showSnapshotPrompt();
      }
    };
    checkSnapshotPrompt();

    // Also check when page becomes visible (tab switch, etc)
    const handleVisibilityChange = () => {
      if (!document.hidden) checkSnapshotPrompt();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedEntityId]);

  const showSnapshotPrompt = () => {
    toast('Assessment session complete', {
      description: 'Save a snapshot to record your current score and track progress over time.',
      duration: Infinity,
      action: { label: 'Save Snapshot', onClick: handleSaveSnapshot },
      cancel: { label: 'Not Now', onClick: () => {} }
    });
  };

  const handleSaveSnapshot = async () => {
    try {
      const res = await maturityAPI.createSnapshot(selectedEntityId);
      toast.success('Snapshot saved successfully');
      // Spec Section 5: offer remediation tasks on assessment completion with low-maturity findings (DPO only)
      if (isDpoRole && res.data?.id) {
        setTaskPromptSnapshotId(res.data.id);
        setTaskPromptOpen(true);
      }
    } catch (error) {
      toast.error('Failed to save snapshot');
    }
  };

  const handleViewDomain = (domainCode) => {
    navigate(`/compliance/maturity/domain/${domainCode}?entity=${selectedEntityId}`);
  };

  const handleExportReport = async () => {
    try {
      toast.info('Generating report...');
      const response = await maturityAPI.downloadReport(selectedEntityId);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TR2UST_Report_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Report downloaded!');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  // Use tier-filtered control count if available.
  // Note: score.assessed_controls is "touched" (includes N/A); for the
  // ProgressCard we want the non-N/A count so Total = Assessed + Pending + N/A
  // balances correctly and matches the DomainCard semantics.
  const totalControls = tierInfo?.total_controls || score?.total_controls || config?.total_controls || 84;
  const assessedControls = score?.total_applicable_controls || 0;
  const naControls = score?.total_na_controls || 0;
  const remainingControls = Math.max(0, totalControls - assessedControls - naControls);

  const pageContent = (
    <div className="p-6 max-w-[1600px] mx-auto space-y-8" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link to="/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 transition-colors">
          <Home className="h-4 w-4" strokeWidth={1.5} />
          <span>Dashboard</span>
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-slate-900 dark:text-white font-medium">Maturity Assessment</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Maturity Assessment
            </h1>
            {tierInfo && (
              <Badge className={`text-xs font-semibold ${
                tierInfo.maturity_tier === 'T1' ? 'bg-blue-500/10 text-blue-600 border-blue-300' :
                tierInfo.maturity_tier === 'T2' ? 'bg-purple-500/10 text-purple-600 border-purple-300' :
                'bg-emerald-500/10 text-emerald-600 border-emerald-300'
              }`}>
                {tierInfo.maturity_tier} - {tierInfo.tier_label}
              </Badge>
            )}
          </div>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            <span>TR<sup>2</sup>UST<sup>®</sup> Framework</span> • {totalControls} Controls across 6 Domains
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExportReport} disabled={!score} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Export
          </Button>
        </div>
      </div>

      {/* Tier Context Banner */}
      {tierInfo && selectedEntityId && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-400">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
            {tierInfo.maturity_tier}
          </span>
          <span>{TIER_BANNER[tierInfo.maturity_tier]}</span>
        </div>
      )}

      {!selectedEntityId ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-slate-500 dark:text-slate-400">Please select an entity to view assessment data</p>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <OverallScoreCard
                score={score?.overall_score}
                level={score?.maturity_level || 1}
                status={score?.compliance_status}
              />
              {/* N/A Score Transparency (Change 5). X = non-N/A assessed
                  (= total_applicable_controls), Y = N/A count. The score is
                  literally based on X only, so X and Y must be disjoint. */}
              {score && (
                (() => {
                  const naRatio = score.total_na_controls / (score.total_controls || 1);
                  const isHighNA = naRatio > NA_ADVISORY_THRESHOLD;
                  return (
                    <p className={`text-sm mt-3 ${isHighNA ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
                      {isHighNA
                        ? `Note: ${score.total_na_controls} controls marked N/A. High N/A counts may affect score reliability.`
                        : `Score based on ${score.total_applicable_controls} assessed controls (${score.total_na_controls} marked Not Applicable).`
                      }
                    </p>
                  );
                })()
              )}
              {/* Score Integrity Signals - Overall Gauge (Change 3) */}
              {score && (
                (() => {
                  const overallCompletion = score.total_controls > 0 ? score.assessed_controls / score.total_controls : 0;
                  return overallCompletion < SCORE_RELIABILITY_THRESHOLD ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-2">
                      Based on {score.assessed_controls} of {score.total_controls} applicable controls — complete assessment for a reliable score.
                    </p>
                  ) : null;
                })()
              )}
            </div>
            <ProgressCard
              total={totalControls}
              assessed={assessedControls}
              remaining={remainingControls}
              na={naControls}
            />
          </div>

          {/* Section Header */}
          <div className="flex items-center justify-between pt-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Domain Overview
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Click a domain to view and assess individual controls
              </p>
            </div>
          </div>

          {/* Domain Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map(domain => (
              <DomainCard
                key={domain.code}
                domain={domain}
                domainScore={score?.domain_scores?.[domain.code]}
                onClick={() => handleViewDomain(domain.code)}
              />
            ))}
          </div>

          {/* Remediation Tasks — Option B: aggregated across all snapshots for this entity */}
          <AssessmentTasksSection
            tasks={assessmentTasks}
            loading={tasksLoading}
            latestSnapshotId={latestSnapshotId}
            clientId={selectedClientId}
            isDpoRole={isDpoRole}
            onNavigate={navigate}
          />
        </>
      )}
    </div>
  );
  
  return (
    <DashboardLayout>
      <AutoTaskPromptModal
        open={taskPromptOpen}
        onOpenChange={setTaskPromptOpen}
        sourceModule="assessment"
        sourceId={taskPromptSnapshotId}
        onCreated={loadAssessmentTasks}
      />
      {pageContent}
    </DashboardLayout>
  );
}
